import {isFinance, isRegularLkHost, isYpla} from 'shared/lib/isFinance';
import {
	getStatusConnectionPaymentMethods,
	isRegistrationStepVisible,
	type RegistrationFlowConfig,
} from './registrationFlow';
import {
	getPaymentSubStepProgress,
	getPaymentSubStepSequence,
	type PaymentSubStep,
} from './paymentFlow';
import type {RegistrationPaymentMethod} from './paymentMethod';
import {RegistrationStep} from './steps';
import {RegistrationWorkStatus} from './types';

/** СМЗ через «Мой налог» / ФНС. На *.finance и ypla те же шаги, тексты Альфа-банка. */
export const requiresMyTaxConnectionFlow = (workStatus: RegistrationWorkStatus | null): boolean => {
	return workStatus === 'self_employment';
};

/** Finance и YPLA: СМЗ через интеграцию Альфа-банк. На `lk.rocketwork.finance` — rocketwork. */
export const isFinanceSelfEmploymentFlow = (workStatus: RegistrationWorkStatus | null): boolean => {
	return workStatus === 'self_employment' && !isRegularLkHost() && (isFinance() || isYpla());
};

export type StatusConnectionNextAction =
	| {type: 'my_tax_access'}
	| {type: 'payment'}
	/** Без Мой налог: PATCH inn/snils → activate (+ PUT current, если первый статус) */
	| {type: 'complete'};

export const getStatusConnectionNextAction = (
	workStatus: RegistrationWorkStatus | null,
	registrationFlow: RegistrationFlowConfig,
): StatusConnectionNextAction => {
	if (requiresMyTaxConnectionFlow(workStatus)) {
		return {type: 'my_tax_access'};
	}

	const paymentMethods = getStatusConnectionPaymentMethods(workStatus, registrationFlow.paymentMethods);

	if (paymentMethods.length > 0) {
		return {type: 'payment'};
	}

	return {type: 'complete'};
};

/**
 * ИНН физлица и ИП уже введён на шаге «Введите ИНН».
 * В подключении статуса его не спрашиваем повторно. Иностранный налоговый идентификатор остаётся.
 */
export const skipsStatusConnectionInn = (
	workStatus: RegistrationWorkStatus | null,
	registrationFlow: RegistrationFlowConfig,
): boolean => {
	return (
		(workStatus === 'individual' || workStatus === 'individual_entrepreneur') &&
		registrationFlow.taxIdType === 'russian_inn'
	);
};

/** Документы перед оплатой/Мой налог: ИНН (опционально) + СНИЛС/код резидента. */
export const getStatusConnectionPrefixSteps = (
	registrationFlow: RegistrationFlowConfig,
	includeInn: boolean,
	includeSnils = true,
): RegistrationStep[] => {
	const steps: RegistrationStep[] = [];

	if (includeInn) {
		steps.push(RegistrationStep.Inn);
	}

	if (includeSnils && isRegistrationStepVisible(registrationFlow.snils)) {
		steps.push(RegistrationStep.Snils);
	}

	if (isRegistrationStepVisible(registrationFlow.residentCode)) {
		steps.push(RegistrationStep.ResidentCode);
	}

	return steps;
};

export const getStatusConnectionSteps = (
	workStatus: RegistrationWorkStatus | null,
	registrationFlow: RegistrationFlowConfig,
	includeInn = true,
	includeSnils = true,
): RegistrationStep[] => {
	// Приветственный экран (MyTaxIntro) и «Мой налог» в пагинацию не входят — только экраны заполнения.
	const steps = getStatusConnectionPrefixSteps(registrationFlow, includeInn, includeSnils);
	const nextAction = getStatusConnectionNextAction(workStatus, registrationFlow);

	// СМЗ: дальше MyTaxAccess — не шаг пагинации.
	if (nextAction.type === 'payment') {
		steps.push(RegistrationStep.PaymentMethod);
	}

	return steps;
};

export type StatusConnectionProgressOptions = {
	/** Экран ИНН в текущем флоу (false — ИНН уже был, экран пропущен). */
	includeInn?: boolean;
	/** Экран СНИЛС в текущем флоу (false — СНИЛС уже был, экран пропущен). */
	includeSnils?: boolean;
	paymentMethod?: RegistrationPaymentMethod | null;
	paymentSubStep?: PaymentSubStep;
};

/**
 * Пагинация подключения статуса.
 * Для ИП с оплатой: БИК + Счёт + Подтверждение — единый счётчик.
 */
export const getStatusConnectionProgress = (
	step: RegistrationStep,
	workStatus: RegistrationWorkStatus | null,
	registrationFlow: RegistrationFlowConfig,
	options: StatusConnectionProgressOptions = {},
): {currentStep: number; totalSteps: number} | null => {
	// СМЗ: ИНН — единственный экран заполнения, без пагинации и «Назад» (только крестик).
	if (requiresMyTaxConnectionFlow(workStatus)) {
		return null;
	}

	const includeInn = options.includeInn ?? true;
	const includeSnils = options.includeSnils ?? true;
	const prefix = getStatusConnectionPrefixSteps(registrationFlow, includeInn, includeSnils);
	const nextAction = getStatusConnectionNextAction(workStatus, registrationFlow);
	const paymentMethods = getStatusConnectionPaymentMethods(workStatus, registrationFlow.paymentMethods);

	// Foreign Tax ID без СНИЛС/кода резидента/оплаты — один экран, только крестик.
	if (
		registrationFlow.taxIdType === 'foreign_tax_id' &&
		prefix.length <= 1 &&
		paymentMethods.length === 0
	) {
		return null;
	}

	if (nextAction.type === 'payment') {
		const paymentMethod = options.paymentMethod ?? (paymentMethods.length === 1 ? paymentMethods[0] : null);

		if (paymentMethod) {
			const paymentSequence = getPaymentSubStepSequence(paymentMethod);
			const totalSteps = prefix.length + paymentSequence.length;

			if (totalSteps <= 1) {
				return null;
			}

			if (
				step === RegistrationStep.Inn ||
				step === RegistrationStep.Snils ||
				step === RegistrationStep.ResidentCode
			) {
				const index = prefix.indexOf(step);

				if (index < 0) {
					return null;
				}

				return {currentStep: index, totalSteps};
			}

			if (
				step === RegistrationStep.PaymentMethod &&
				options.paymentSubStep &&
				options.paymentSubStep !== 'select'
			) {
				return getPaymentSubStepProgress(paymentMethod, options.paymentSubStep, prefix.length);
			}

			return null;
		}
	}

	const steps = getStatusConnectionSteps(workStatus, registrationFlow, includeInn, includeSnils);

	// Один экран — без пагинации.
	if (steps.length <= 1) {
		return null;
	}

	const index = steps.indexOf(step);

	if (index < 0) {
		return null;
	}

	return {currentStep: index, totalSteps: steps.length};
};

/** @deprecated use getStatusConnectionNextAction */
export const getStatusConnectionAfterDocumentsStep = (
	workStatus: RegistrationWorkStatus | null,
	registrationFlow: RegistrationFlowConfig,
): RegistrationStep => {
	const action = getStatusConnectionNextAction(workStatus, registrationFlow);

	if (action.type === 'my_tax_access') {
		return RegistrationStep.MyTaxAccess;
	}

	if (action.type === 'payment') {
		return RegistrationStep.PaymentMethod;
	}

	return RegistrationStep.MyTaxSuccess;
};

/** @deprecated use buildStatusConnectionIntroCredentials */
export const buildIndividualIntroCredentials = (registrationFlow: RegistrationFlowConfig): string => {
	return buildStatusConnectionIntroCredentials('individual', registrationFlow);
};

/** Список того, что реально нужно ввести при подключении статуса (по конфигу флоу). */
export const buildStatusConnectionIntroCredentials = (
	workStatus: RegistrationWorkStatus | null,
	registrationFlow: RegistrationFlowConfig,
): string => {
	const parts: string[] = [];

	if (!skipsStatusConnectionInn(workStatus, registrationFlow)) {
		if (registrationFlow.taxIdType === 'russian_inn') {
			parts.push('номер ИНН');
		} else {
			parts.push('налоговый идентификатор');
		}
	}

	if (isRegistrationStepVisible(registrationFlow.snils)) {
		parts.push('номер вашего СНИЛС');
	}

	if (isRegistrationStepVisible(registrationFlow.residentCode)) {
		parts.push('код сертификата резидента РФ');
	}

	if (workStatus === 'individual_entrepreneur') {
		parts.push('данные вашего счёта индивидуального предпринимателя');
	}

	return parts.join(', ');
};
